import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';

export const CookiePolicyPage = () => {
  usePageMeta({
    title: 'Cookie Policy',
    description: 'Digitive Health cookie policy. Understand how we use cookies and tracking technologies on our website.',
    path: '/cookie-policy',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="cookie-policy-page">
      {/* Header */}
      <Header />

      {/* Content */}
      <section className="pt-28 pb-16">
        <div className="container-width">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-8">
              Cookie Policy
            </h1>
            <p className="text-muted-foreground mb-8">
              Last Updated: February 2025
            </p>

            <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. What Are Cookies?</h2>
                <p>
                  Cookies are small text files that are placed on your computer or mobile device when you 
                  visit a website. They are widely used to make websites work more efficiently and to provide 
                  information to website owners. Digitive Health uses cookies and similar technologies 
                  to enhance your experience on our website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Types of Cookies We Use</h2>
                
                <h3 className="text-xl font-medium text-foreground mb-3">Essential Cookies</h3>
                <p>
                  These cookies are necessary for the website to function properly. They enable core 
                  functionality such as security, network management, and account access. You cannot 
                  opt out of these cookies as the website cannot function without them.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Session management cookies</li>
                  <li>Authentication cookies</li>
                  <li>Security cookies</li>
                  <li>Load balancing cookies</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">Performance Cookies</h3>
                <p>
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously. This helps us improve our website's performance.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Analytics cookies (e.g., Google Analytics)</li>
                  <li>Error tracking cookies</li>
                  <li>Page load time monitoring</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">Functional Cookies</h3>
                <p>
                  These cookies enable enhanced functionality and personalization, such as remembering 
                  your preferences and settings.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Language preference cookies</li>
                  <li>Location preference cookies</li>
                  <li>User preference cookies</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">Marketing Cookies</h3>
                <p>
                  These cookies are used to track visitors across websites to display relevant 
                  advertisements. They help us measure the effectiveness of our marketing campaigns.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Advertising cookies</li>
                  <li>Social media cookies</li>
                  <li>Retargeting cookies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Third-Party Cookies</h2>
                <p>
                  Some cookies on our website are set by third-party services. These include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Analytics:</strong> For website traffic analysis</li>
                  <li><strong>LinkedIn:</strong> For social media integration and recruitment features</li>
                  <li><strong>CEIPAL:</strong> For our applicant tracking system job board</li>
                  <li><strong>Google Maps:</strong> For displaying our office location</li>
                </ul>
                <p className="mt-4">
                  We do not control these third-party cookies. Please refer to the respective privacy 
                  policies of these third parties for more information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. How to Manage Cookies</h2>
                <p>
                  You can control and manage cookies in several ways:
                </p>
                
                <h3 className="text-xl font-medium text-foreground mb-3 mt-4">Browser Settings</h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>View what cookies are stored on your device</li>
                  <li>Delete all or specific cookies</li>
                  <li>Block all cookies or cookies from specific websites</li>
                  <li>Set preferences for certain types of cookies</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-4">Cookie Consent Banner</h3>
                <p>
                  When you first visit our website, you will see a cookie consent banner that allows 
                  you to accept or reject non-essential cookies. You can change your preferences at 
                  any time by clicking the cookie settings link in our footer.
                </p>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-4">Opt-Out Links</h3>
                <p>For specific third-party cookies, you can opt out using these links:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline">
                      Google Analytics Opt-Out
                    </a>
                  </li>
                  <li>
                    <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer"
                       className="text-primary hover:underline">
                      Digital Advertising Alliance Opt-Out
                    </a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Impact of Disabling Cookies</h2>
                <p>
                  If you disable or reject cookies, some parts of our website may not function properly. 
                  Specifically:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You may not be able to log into your account</li>
                  <li>Some forms may not work correctly</li>
                  <li>Your preferences will not be saved</li>
                  <li>Some features of our job board may be limited</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Healthcare Data Protection</h2>
                <p>
                  As a healthcare staffing company, we take additional precautions with any data that 
                  may be related to healthcare services. Cookies on our website do not collect or store 
                  any Protected Health Information (PHI) or sensitive medical data. Our cookie practices 
                  comply with HIPAA requirements where applicable.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Contact Us</h2>
                <p>
                  If you have questions about our use of cookies, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-secondary rounded-lg">
                  <p><strong>Digitive Health Inc.</strong></p>
                  <p>11501 Dublin Blvd STE 200</p>
                  <p>Dublin, CA 94568</p>
                  <p>Phone: (408) 935-8699</p>
                  <p>Email: sales@digitivehealth.com</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Updates to This Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in technology, 
                  legislation, or our data practices. We will post any changes on this page and update 
                  the "Last Updated" date.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
